import { DEFAULT_FILL_OPACITY } from '../../../../constants.js';
import { BarCanvasInstance } from '../../types/canvas-shape.js';
import { getCanvasColor } from '../canvas-color.js';

export type FnCanvasAccessor<Datum> = (d: Datum, i: number) => number;

type FnBarCanvasStyle<Datum> = (
  ctx: CanvasRenderingContext2D,
  barCanvas: BarCanvasInstance<Datum>,
  x: FnCanvasAccessor<Datum>,
  y: FnCanvasAccessor<Datum>,
  width: FnCanvasAccessor<Datum>,
  height: FnCanvasAccessor<Datum>,
) => void;

/**
 * Builds style FN Bar based on parameters.
 *
 * @param strokeWidth - stroke width
 * @returns - fn print Bar canvas
 */
export function printBarBuilder<Datum>(
  strokeWidth: number = 1,
): FnBarCanvasStyle<Datum> {
  return (
    ctx,
    barCanvas,
    xAccessor,
    yAccessor,
    widthAccessor,
    heightAccessor,
  ) => {
    const {
      color,
      data,
      opacity = () => DEFAULT_FILL_OPACITY,
      defined,
    } = barCanvas;

    ctx.beginPath();
    ctx.filter = 'none';

    for (let index = 0; index < data.length; index++) {
      const datum = data[index];
      if (defined && !defined(datum)) {
        continue;
      }

      ctx.fillStyle = getCanvasColor(color(datum));
      ctx.globalAlpha = opacity(datum);

      let width = widthAccessor(datum, index);
      let height = heightAccessor(datum, index);
      let x = xAccessor(datum, index);
      let y = yAccessor(datum, index);

      // + | +
      if (width > 0 && height > 0) {
        x += strokeWidth;
        y += strokeWidth;
        width = Math.max(width - strokeWidth, strokeWidth);
        height = Math.max(height - strokeWidth, strokeWidth);
        // + | -
      } else if (width > 0 && height < 0) {
        x += strokeWidth;
        y -= strokeWidth;
        width = Math.max(width - strokeWidth, strokeWidth);
        height += strokeWidth;
      } else if (width < 0 && height > 0) {
        // - | +
        x -= strokeWidth;
        y += strokeWidth;
        width += strokeWidth;
        height = Math.max(height - strokeWidth, strokeWidth);
      } else if (width < 0 && height < 0) {
        // - | -
        x -= strokeWidth;
        y -= strokeWidth;
        width += strokeWidth;
        height += strokeWidth;
      }

      ctx.fillRect(x, y, width, height);
    }
  };
}
